import { useCallback, useEffect, useReducer } from 'react';
import http from 'hub-http/clients/apiClient';
import usePrevious from 'react-utils/hooks/usePrevious';
import * as RequestStatus from 'ContentUtils/constants/RequestStatus';
const MAX_RETRIES = 3;
const FINISHED_STATUSES = [RequestStatus.SUCCEEDED, RequestStatus.FAILED];
const initialState = {
  requestStatus: RequestStatus.UNINITIALIZED,
  isPolling: false,
  hasFinishedPrerendering: false,
  numFailedRequests: 0
};
const ActionTypes = {
  RECEIVED_PRERENDERING_PROGRESS: 'RECEIVED_PRERENDERING_PROGRESS',
  REQUESTED_PRERENDERING_PROGRESS: 'REQUESTED_PRERENDERING_PROGRESS',
  REQUESTED_PRERENDERING_PROGRESS_FAILED: 'REQUESTED_PRERENDERING_PROGRESS_FAILED',
  START_POLLING: 'START_POLLING',
  STOP_POLLING: 'STOP_POLLING'
};
const requestReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.START_POLLING:
      return Object.assign({}, state, {
        isPolling: true
      });
    case ActionTypes.STOP_POLLING:
      return Object.assign({}, state, {
        isPolling: false
      });
    case ActionTypes.REQUESTED_PRERENDERING_PROGRESS_FAILED:
      return Object.assign({}, state, {
        requestStatus: RequestStatus.FAILED,
        numFailedRequests: state.numFailedRequests + 1
      });
    case ActionTypes.REQUESTED_PRERENDERING_PROGRESS:
      return Object.assign({}, state, {
        requestStatus: RequestStatus.PENDING
      });
    case ActionTypes.RECEIVED_PRERENDERING_PROGRESS:
      return Object.assign({}, state, {
        hasFinishedPrerendering: action.payload.hasFinishedPrerendering,
        requestStatus: RequestStatus.SUCCEEDED
      });
    default:
      return state;
  }
};
/*
 * /cms/v3/pre-rendering-progress returns an object where the keys are in
 * the following format:
 * `CONTENT:${contentId}|${contentType}:${contentId}:${url}`
 * Since we send the id and target query params, the response should be an
 * object with only one key.
 */
const getPrerenderingStatusObjectFromResponseData = data => {
  if (data && typeof data === 'object') {
    const dataKeys = Object.keys(data);
    const firstObjectKey = dataKeys && dataKeys.length === 1 ? dataKeys[0] : null;
    if (firstObjectKey && firstObjectKey in data) {
      return data[firstObjectKey][0];
    }
  }
  return {
    status: null,
    updated: 0
  };
};
const useFetchContentPrerenderingProgress = ({
  id,
  contentType,
  publishDate = 0,
  url,
  onPollingComplete = () => {}
}) => {
  const [state, dispatch] = useReducer(requestReducer, initialState);
  const {
    hasFinishedPrerendering,
    requestStatus,
    isPolling,
    numFailedRequests
  } = state;
  const prevRequestStatus = usePrevious(requestStatus);
  const pollCmsPrerendingProgress = useCallback(() => {
    dispatch({
      type: ActionTypes.REQUESTED_PRERENDERING_PROGRESS
    });
    http.get('cms/v3/pre-rendering-progress', {
      query: {
        id: `CONTENT:${id}`,
        target: `${contentType}:${id}:${url}`
      }
    }).then(data => {
      const {
        status,
        updated
      } = getPrerenderingStatusObjectFromResponseData(data);
      dispatch({
        payload: {
          hasFinishedPrerendering: status === 'DONE' && updated >= publishDate
        },
        type: ActionTypes.RECEIVED_PRERENDERING_PROGRESS
      });
    }).catch(e => {
      console.error(e);
      dispatch({
        type: ActionTypes.REQUESTED_PRERENDERING_PROGRESS_FAILED
      });
    });
  }, [contentType, id, publishDate, url]);
  useEffect(() => {
    dispatch({
      type: ActionTypes.START_POLLING
    });
    pollCmsPrerendingProgress();
  }, [pollCmsPrerendingProgress, dispatch]);
  useEffect(() => {
    if (prevRequestStatus === RequestStatus.PENDING && FINISHED_STATUSES.includes(requestStatus)) {
      if (hasFinishedPrerendering) {
        dispatch({
          type: ActionTypes.STOP_POLLING
        });
        onPollingComplete();
      } else if (numFailedRequests < MAX_RETRIES) {
        setTimeout(() => pollCmsPrerendingProgress(), 2000);
      }
    }
  }, [dispatch, hasFinishedPrerendering, numFailedRequests, pollCmsPrerendingProgress, prevRequestStatus, requestStatus, onPollingComplete]);
  return {
    isPolling,
    hasFinishedPrerendering
  };
};
export default useFetchContentPrerenderingProgress;